import { Form } from "@circle-react-uikit/Form";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import type { FieldElement } from "@/react/types/Element";

import { useFieldElementRules } from "../useFieldElementRules";

interface ChoiceElementProps {
  defaultValue?: string | string[] | null;
  element: FieldElement;
  errors: Record<number, string>;
  fieldIndex: number;
}

export const ChoiceElement = ({
  defaultValue,
  element,
  fieldIndex,
  errors,
}: ChoiceElementProps) => {
  const { setError, setValue } = useFormContext();
  const idName = `elements[${fieldIndex}][id]`;
  const valueName = `elements[${fieldIndex}][value]`;
  const rules = useFieldElementRules(element);

  // Set initial value if defaultValue is provided
  useEffect(() => {
    if (defaultValue) {
      setValue(valueName, defaultValue, { shouldDirty: false });
    }
  }, [valueName, setValue, defaultValue]);

  useEffect(() => {
    if (errors && errors[fieldIndex]) {
      setError(`elements[${fieldIndex}][value]`, {
        type: "server",
        message: errors[fieldIndex],
      });
    }
  }, [errors, fieldIndex, setError]);

  const options = (element.attributes.options || []).map(option => ({
    label: option,
    value: option,
  }));

  const isMultiple = element.attributes.field_type === "multiple_choice";

  return (
    <>
      <Form.Item
        label={element.attributes.label}
        name={valueName}
        rules={rules}
        hideDescription
        hideBorder
        forceRequiredLabel={element.attributes.required}
        labelClassName="!mb-4 [overflow-wrap:anywhere]"
        errorClassName="form-group__error !mb-0"
      >
        {isMultiple ? (
          <Form.CheckboxGroup
            name={valueName}
            options={options}
            itemLabelClassName="[overflow-wrap:anywhere]"
          />
        ) : (
          <Form.RadioGroup
            name={valueName}
            variant="radio"
            options={options}
            itemLabelClassName="[overflow-wrap:anywhere]"
          />
        )}
      </Form.Item>
      <Form.Input
        wrapperClassName="hidden"
        type="hidden"
        name={idName}
        value={element.id}
      />
    </>
  );
};
